import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { addToast, removeToast } from '../../redux/slices/toastSlice';
import { clearError } from '../../redux/slices/masterDataSlice';
import PropTypes from 'prop-types';
import Header from './Header';
import Sidebar from './Sidebar';
import { AppleToast } from '../ui/MySwal';
import ErrorLogModal from '../modals/ErrorLogModal'
import { useLanguage } from '../../contexts/LanguageContext';
import { cn } from '../../utils/cn';

const Layout = ({ children, className }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showErrorLog, setShowErrorLog] = useState(false);

  const toasts = useSelector((state) => state.toast);
  const dispatch = useDispatch();
  const { t } = useLanguage();

  // Handle responsive behavior
  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 1024; // lg breakpoint
      setIsMobile(mobile);
      
      // Reset collapsed state on mobile
      if (mobile) {
        setSidebarCollapsed(false);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, [sidebarOpen]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const toggleSidebarCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // Handle message toast
  useEffect(() => {
    if (toasts.length > 0) {
      toasts.forEach((toast) => {
        let html = toast.message;
        const timer = toast.showViewLog ? 10000 : 3000;
        if (toast.showViewLog) {
          html = `<div>${toast.message}<br/><a href="#" id="view-error-log">${t('errorLog.viewLog', 'View Log')}</a></div>`;
        }
        AppleToast.fire({
          html,
          timer: toast.duration || timer,
          icon: toast.type,
          showCloseButton: true,
          didOpen: () => {
            const viewLogLink = document.getElementById("view-error-log");
            if (viewLogLink) {
              viewLogLink.addEventListener("click", (e) => {
                e.preventDefault();
                setShowErrorLog(true);
                AppleToast.close();
              });
            }
          },
        }).then(() => dispatch(removeToast(toast.id)));
      });
    }
  }, [toasts, dispatch, t]);

  return (
    <div className={cn("min-h-screen bg-background", className)}>
      {/* Header */}
      <Header 
        onMenuToggle={toggleSidebar}
        onSidebarToggle={toggleSidebarCollapse}
        sidebarCollapsed={sidebarCollapsed}
      />
      
      <div className="flex">
        {/* Sidebar */}
        <div className="min-h-[calc(100vh-64px)] z-50">
        <Sidebar 
          isOpen={sidebarOpen} 
          onClose={closeSidebar}
          isCollapsed={!isMobile && sidebarCollapsed}
        />
        </div>
        {/* Main content */}
        <main className={cn(
          "flex-1 overflow-auto",
          "transition-all duration-300 ease-in-out"
        )}>
          <div className="p-6">
            {children}
          </div>
        </main>
      </div>
      {/* Error Log Modal */}
      <ErrorLogModal
        isOpen={showErrorLog}
        onClose={() => setShowErrorLog(false)}
      />
    </div>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

export default Layout;